<template>
  <CForm @submit="save">
    <CModal :title="$t('jobModal.title')" @close="close">
      <template #body>
        <CField type="textarea" :disabled="!job.description.editable" v-model="formData.job.description.content" :label="$t('jobModal.description')"/>

        <CFlex justify="between" align="end">
          <CLabel class="u-m-0">{{ $t('jobModal.clientComplaint') }}</CLabel>
          <CButton theme="ghost-primary" @click="formData.diagnostic.complaint = formData.job.description.content">{{ $t('jobModal.copyDescription') }}</CButton>
        </CFlex>
        <CField type="textarea" v-model="formData.diagnostic.complaint" :disabled="complaintDisabled" class="u-mt-0"/>

        <CField type="textarea" v-model="formData.diagnostic.conditions" :disabled="complaintDisabled" :label="$t('jobModal.reproduceCondition')"/>

        <CJobDmsPrice v-if="job.price.dmsAmountWithVat !== null" :job/>

        <CJobPriceAmountFields v-model:amountWithVat="formData.job.price.amountWithVat" v-model:amountWithoutVat="formData.job.price.amountWithoutVat"
                               v-model:warrantyStatus="formData.job.warrantyStatus" :job class="u-mb-5"/>

        <CMediaSection :context="mediaContext" :targetData="mediaTargetData" showAll/>
      </template>
    </CModal>
  </CForm>
</template>
<script setup lang="ts">
  import { computed, reactive } from 'vue';
  import CForm from '@/shared/components/form/CForm.vue';
  import CModal from '@/general/components/CModal.vue';
  import CField from '@/shared/components/form/CField.vue';
  import CJobDmsPrice from '@/jobs/components/CJobDmsPrice.vue';
  import CJobPriceAmountFields from '@/jobs/components/CJobPriceAmountFields.vue';
  import Job from '@/jobs/models/Job';
  import CFlex from '@/shared/components/CFlex.vue';
  import CLabel from '@/shared/components/form/CLabel.vue';
  import CButton from '@/shared/components/buttons/CButton.vue';
  import CMediaSection from '@/media/components/CMediaSection.vue';
  import MediaTargetData from '@/general/models/MediaTargetData';
  import EntityType from '@/general/constants/EntityType';
  import MediaContext2 from '@/general/models/MediaContext2';
  import Diagnostic, { ComplaintDiagnostic } from '@/jobs/models/diagnosis/Diagnostic';
  import JobContext from '@/jobs/models/JobContext';
  import DiagnosticType from '@/jobs/constants/DiagnosticType';
  import { isPresent, present } from '@/shared/utils/typescript';
  import CheckInService from '@/dossier/services/CheckInService';
  import { DiagnosticTypeDescriptionId } from '@/jobs/models/diagnosis/DiagnosticTypeDescription';

  const props = defineProps<{
    job: Job;
    context: JobContext;
    mediaContext: MediaContext2;
    diagnosticTypeDescriptionId?: DiagnosticTypeDescriptionId | null;
  }>();

  const emit = defineEmits<{ (event: 'close'): void }>();

  const jobClone = props.job.clone();

  const formData = reactive({
    job: jobClone,
    diagnostic: jobClone.getComplaintDiagnostic()
      ?? Diagnostic.fromTypeDescription(present(props.context.diagnosticTypes.find(t => t.type === DiagnosticType.COMPLAINT))),
  }) as { job: Job, diagnostic: ComplaintDiagnostic };

  const mediaTargetData = computed(() => new MediaTargetData(EntityType.JOB, props.job.id));

  const complaintDisabled = computed(() => !props.job.getComplaintDiagnostic() && !isPresent(props.diagnosticTypeDescriptionId));

  function save() {
    CheckInService.saveJobEdit(props.context, formData);
    emit('close');
  }

  function close() {
    emit('close');
  }
</script>